// Question-first FAQ templates for destination pages — keyed by category
// and common tags. Answers should feel like a knowledgeable local friend.

const BASE_FAQS = [
	{ q: 'Is this worth my limited travel days?', a: 'Check the "Why visit" and difficulty sections above. Nepal rewards depth over rushing — if you only have a few days, prioritize what genuinely excites you over a checklist.' },
	{ q: 'Do I need a guide?', a: 'Cultural sites and easy city attractions are fine independently. Treks, national parks, and restricted areas (Upper Mustang, parts of Manang) legally require a licensed guide or organized trek — confirm for your specific route on the Government Hub.' },
	{ q: 'What should I pack?', a: 'Layers matter more than any single item. See the Seasonal Discovery hub for month-by-month packing guidance matched to your travel window.' },
];

const CATEGORY_FAQS = {
	national_park: [
		{ q: 'Do I need a park entry permit?', a: 'Yes — all of Nepal\'s major national parks require an entry permit, usually obtained in Kathmandu or at the park gate. Fees fund conservation. Your trekking agency typically bundles this; independent travelers should get it through official channels.' },
		{ q: 'Is a guide mandatory inside the park?', a: 'Chitwan safaris require a licensed guide. Trekking parks like Sagarmatha have additional permit requirements beyond the entry fee — see TIMS and park-specific rules on the Government Hub.' },
		{ q: 'When is wildlife viewing best?', a: 'October through March offers the most comfortable temperatures and reliable sightings in Chitwan. Early-morning canoe trips consistently outperform afternoon jeep safaris.' },
	],
	unesco_site: [
		{ q: 'What is the entry fee?', a: 'UNESCO sites in the Kathmandu Valley charge separate foreign-visitor and Nepali-citizen rates — this is standard practice, not a scam. Some sites add a camera fee beyond the entrance ticket.' },
		{ q: 'How long should I spend here?', a: 'Most valley durbar squares reward a half-day, not a rushed hour. Patan\'s on-site museum alone justifies a slower visit than most day-tour itineraries allow.' },
		{ q: 'What should I wear?', a: 'Modest clothing covering shoulders and knees. Remove shoes before entering temple interiors. At active cremation sites like Pashupatinath, be a quiet, respectful observer — not a close-up photographer.' },
	],
	viewpoint: [
		{ q: 'How fit do I need to be?', a: 'Check the difficulty rating above. "Strenuous" means multi-day high-altitude trekking with real altitude-sickness risk — not a casual day hike. Build acclimatization days into your plan.' },
		{ q: 'What about altitude sickness?', a: 'Symptoms above 3,000m are common and can be serious. Know the signs (headache, nausea, confusion), never ascend with worsening symptoms, and confirm your travel insurance covers helicopter evacuation before you go.' },
		{ q: 'Are Lukla flights reliable?', a: 'Mountain flying is genuinely weather-dependent. Delays of one to three days are routine in peak season and worse in monsoon. Never plan to start trekking the same day you fly into Lukla.' },
	],
	temple: [
		{ q: 'Can non-Hindus enter?', a: 'Varies by site. Pashupatinath\'s main temple is Hindu-only, but the complex and cremation ghats are viewable from across the river. Always ask or observe signage before entering sacred inner areas.' },
		{ q: 'When is the best time to visit?', a: 'Early morning, before tour groups arrive, is quieter and more respectful of worshippers. Evening lamp-lighting at Buddhist stupas draws crowds but has the best atmosphere.' },
	],
	lake: [
		{ q: 'When are mountain reflections best?', a: 'Dawn — by mid-morning, breeze ripples the surface. Phewa Lake\'s Annapurna reflection is a sunrise phenomenon, not a sunset one.' },
		{ q: 'How much does boating cost?', a: 'Agree on price and duration before getting in the boat, not after. This is the most common complaint visitors report at lakeside destinations.' },
	],
	other: [
		{ q: 'Do I need a special permit?', a: 'Restricted areas like Upper Mustang require a separate permit issued only through registered trekking agencies, at significantly higher cost than standard routes. Confirm before booking.' },
	],
};

const TAG_FAQS = {
	'family-friendly': { q: 'Is this good for families?', a: 'This destination is tagged family-friendly — generally accessible without extreme altitude or strenuous hiking. Still check difficulty, altitude, and opening hours for young children.' },
	'senior-friendly': { q: 'Is this suitable for older travelers?', a: 'Tagged senior-friendly — typically lower physical demand. Check accessibility notes and whether stairs or uneven terrain are involved.' },
	'solo-travel-friendly': { q: 'Is solo travel safe here?', a: 'Nepal is generally safe for solo travelers in tourist areas. On treks, going with a guide or group is strongly recommended for safety and permit compliance, even if you prefer traveling alone in cities.' },
	trekking: { q: 'Can I trek independently?', a: 'Some routes allow independent trekking with a TIMS card. Restricted areas and many national park corridors require a licensed guide by law. Rules vary considerably by region — confirm on the Government Hub.' },
};

export function faqsForDestination(destination) {
	const faqs = [...BASE_FAQS];
	const catFaqs = CATEGORY_FAQS[destination.category] || CATEGORY_FAQS.other;
	faqs.push(...catFaqs);
	(destination.tags || []).forEach((tag) => {
		if (TAG_FAQS[tag.slug]) faqs.push(TAG_FAQS[tag.slug]);
	});
	// Deduplicate by question
	const seen = new Set();
	return faqs.filter((f) => {
		if (seen.has(f.q)) return false;
		seen.add(f.q);
		return true;
	});
}

// Suggested photo spots by category — editorial until photo POI model exists
export const PHOTO_SPOTS = {
	viewpoint: ['Summit viewpoint at dawn for alpenglow', 'Trail ridgelines with prayer flags in the foreground', 'Teahouse terraces facing the massif at sunset'],
	unesco_site: ['Courtyard architecture from elevated café rooftops', 'Early-morning empty squares before tour groups', 'Detail shots of wood carving and metalwork'],
	lake: ['Dawn shore for mountain reflections', 'Boat perspective toward the island temple', 'Lakeside café terraces at golden hour'],
	national_park: ['Canoe-level wildlife shots at dawn', 'Jungle trail light shafts in early morning', 'Bird hides and wetland edges'],
	temple: ['Designated viewing areas only — never photograph cremations up close', 'Wide shots of the complex from across the river', 'Incense and butter-lamp details in evening light'],
	default: ['Golden hour for warm light on architecture', 'Early morning for fewer crowds and cleaner compositions', 'Include local context — prayer flags, daily life, food stalls'],
};

export function photoSpotsFor(destination) {
	return PHOTO_SPOTS[destination.category] || PHOTO_SPOTS.default;
}

// Natural "next question" flow per destination type
export function knowledgeFlowFor(destination) {
	const flow = [];
	flow.push({ label: 'When is the best time to visit?', to: '/seasonal-discovery', desc: 'Month-by-month weather, festivals, and trekking conditions' });
	if (destination.category === 'viewpoint' || (destination.tags || []).some((t) => t.slug === 'trekking')) {
		flow.push({ label: 'What permits do I need?', to: '/government', desc: 'TIMS, park entry, and restricted-area permits' });
	}
	flow.push({ label: 'How do I get around Nepal?', to: '/transportation', desc: 'Flights, buses, jeeps, and ride-apps' });
	if (destination.best_season) {
		flow.push({ label: 'Plan a full itinerary', to: '/seasonal-discovery#itineraries', desc: 'Suggested routes that match the season' });
	}
	flow.push({ label: 'Book a guided experience', to: '/packages', desc: 'Private tours customized to your dates' });
	return flow;
}

// Related guide slugs by destination category
export const RELATED_GUIDES = {
	national_park: ['tims-card-trekking-permits', 'best-time-to-visit-nepal'],
	viewpoint: ['tims-card-trekking-permits', 'kathmandu-to-pokhara-your-options', 'best-time-to-visit-nepal'],
	unesco_site: ['best-time-to-visit-nepal', 'understanding-nepals-festival-calendar'],
	temple: ['understanding-nepals-festival-calendar', 'best-time-to-visit-nepal'],
	lake: ['kathmandu-to-pokhara-your-options', 'best-time-to-visit-nepal'],
	other: ['tims-card-trekking-permits', 'best-time-to-visit-nepal'],
	default: ['best-time-to-visit-nepal', 'nepal-tourist-visa'],
};

export function relatedGuideSlugs(destination) {
	const slugs = RELATED_GUIDES[destination.category] || RELATED_GUIDES.default;
	if (destination.category === 'national_park' && destination.district && destination.district.name === 'Chitwan') {
		return ['best-time-to-visit-nepal', 'nepal-tourist-visa'];
	}
	return slugs;
}
